import { VendingMachineInfo } from '../types/types';
import SessionStatusCodes from '../constants/SessionStatusCodes';

export type Slave = NonNullable<VendingMachineInfo['vendingMachineSlaves']>[number];

type Slaves = Slave[] | null | undefined;

// The visit's connector state, kept in VendingMachineContext next to stickerId
export type SlaveVisit = { stickerSlaveId: string | null; confirmedSlaveId: string | null };

// none: the machine has no slaves; leafId: the connector to use; pickerParentId: show the picker at that level
export type SlaveSelection = { none: true }
  | { leafId: string; parentId: string | null }
  | { pickerParentId: string | null };

// What VMView does next for an EV charger: go on, check a connector's availability, or show the picker
export type SlaveGateStep = { ready: true }
  | { check: string; parentId: string | null }
  | { pickerParentId: string | null };

const hasChildren = (slaves: Slave[], id: string) => slaves.some((s) => s.parentId === id);

export const getLeafSlaves = (slaves: Slave[]) => slaves.filter((s) => !hasChildren(slaves, s.id));

// The server drops inactive slaves, so a connector can point at a charger missing from the list: treat it as top level
export const detachOrphanSlaves = (slaves: Slave[]): Slave[] => slaves.map((s) => (
  s.parentId !== null && !slaves.some((p) => p.id === s.parentId) ? { ...s, parentId: null } : s
));

const getDescendantLeaves = (slaves: Slave[], parentId: string | null, seen = new Set<string>()): Slave[] => (
  slaves
    .filter((s) => s.parentId === parentId && !seen.has(s.id))
    .reduce<Slave[]>((leaves, child) => {
      seen.add(child.id);
      return hasChildren(slaves, child.id)
        ? leaves.concat(getDescendantLeaves(slaves, child.id, seen))
        : leaves.concat(child);
    }, [])
);

// The server flags the slave the sticker is bound to; read it once, when the machine loads.
// It flags at most one today; should a charger and a connector both be flagged, the connector wins
export const getStickerSlaveId = (slaves: Slaves): string | null => {
  const flagged = (slaves || []).filter((s) => s.isSelected);
  const flaggedLeaf = flagged.find((s) => !hasChildren(slaves || [], s.id));
  return (flaggedLeaf ?? flagged[0])?.id ?? null;
};

/**
 * Decides which connector a session uses from the sticker binding: a connector is used as is, a charger
 * scopes the choice to its own connectors. parentId is the level the picker shows that connector at.
 */
export const resolveSlaveSelection = (rawSlaves: Slaves, stickerSlaveId: string | null): SlaveSelection => {
  if (!rawSlaves || rawSlaves.length === 0) {
    return { none: true };
  }
  const slaves = detachOrphanSlaves(rawSlaves);

  const sticker = slaves.find((s) => s.id === stickerSlaveId);
  if (sticker && !hasChildren(slaves, sticker.id)) {
    return { leafId: sticker.id, parentId: sticker.parentId };
  }

  const scopeId = sticker ? sticker.id : null;
  const candidates = getDescendantLeaves(slaves, scopeId);
  if (candidates.length === 1) {
    return { leafId: candidates[0].id, parentId: candidates[0].parentId };
  }

  return { pickerParentId: scopeId };
};

export const getSlaveGateStep = (slaves: Slaves, visit: SlaveVisit): SlaveGateStep => {
  const selection = resolveSlaveSelection(slaves, visit.stickerSlaveId);
  if ('none' in selection) {
    return { ready: true };
  }
  // Already checked and chosen in this visit (e.g. back from /mbway)
  if (slaves && getLeafSlaves(slaves).some((s) => s.id === visit.confirmedSlaveId)) {
    return { ready: true };
  }
  if ('pickerParentId' in selection) {
    return selection;
  }
  return { check: selection.leafId, parentId: selection.parentId };
};

// isSelected is the current choice that SessionContext and TopHeader read
export const selectOnly = <T extends { vendingMachineSlaves?: Slaves }>(info: T, leafId: string): T => ({
  ...info,
  vendingMachineSlaves: info.vendingMachineSlaves?.map((s) => ({ ...s, isSelected: s.id === leafId })),
});

export const clearSelection = <T extends { vendingMachineSlaves?: Slaves }>(info: T): T => ({
  ...info,
  vendingMachineSlaves: info.vendingMachineSlaves?.map((s) => ({ ...s, isSelected: false })),
});

// The picker level that shows the given connector
export const getPickerParentId = (slaves: Slaves, slaveId: string | null): string | null => (
  detachOrphanSlaves(slaves || []).find((s) => s.id === slaveId)?.parentId ?? null
);

export const formatSlaveLabel = (slaves: Slave[], slave: Slave, identificationNumber?: string) => {
  const parent = slave.parentId !== null ? slaves.find((s) => s.id === slave.parentId) : undefined;
  return parent
    ? `#${parent.number} | ${parent.name} > #${slave.number} | ${slave.name}`
    : `${identificationNumber} > #${slave.number} | ${slave.name}`;
};

export const isSlaveAvailable = (state?: string) => state === 'AVAILABLE' || state === 'FREE';

// The connectors the scanned sticker allows: its own connector, its charger's connectors, or every one
const getStickerScopeLeaves = (rawSlaves: Slave[], stickerSlaveId: string | null): Slave[] => {
  const slaves = detachOrphanSlaves(rawSlaves);
  const sticker = slaves.find((s) => s.id === stickerSlaveId);
  if (!sticker) {
    return getLeafSlaves(slaves);
  }
  return hasChildren(slaves, sticker.id) ? getDescendantLeaves(slaves, sticker.id) : [sticker];
};

// The picker level the customer cannot go above: the sticker's charger, or the charger of the sticker's
// connector; the top level for a machine-level sticker (product decision, VENDIOT-7999 plan revision 3)
export const getPickerScopeRoot = (rawSlaves: Slaves, stickerSlaveId: string | null): string | null => {
  const slaves = detachOrphanSlaves(rawSlaves || []);
  const sticker = slaves.find((s) => s.id === stickerSlaveId);
  if (!sticker) {
    return null;
  }
  return hasChildren(slaves, sticker.id) ? sticker.id : sticker.parentId;
};

// The connectors the picker can offer inside that scope
export const getPickerScopeLeaves = (rawSlaves: Slaves, stickerSlaveId: string | null): Slave[] => {
  const slaves = detachOrphanSlaves(rawSlaves || []);
  return getDescendantLeaves(slaves, getPickerScopeRoot(slaves, stickerSlaveId));
};

// The connector of the customer's session, when the scanned sticker allows it: the gate asks the server
// whether that session is still live before refusing its own connector as busy. A sticker naming another
// connector or charger is a new intent, so it gets the normal flow
export const getLiveSessionCandidate = (
  slaves: Slaves,
  stickerSlaveId: string | null,
  session: { sessionId?: string | null; sessionSlaveId?: string | null },
): string | null => {
  if (!session.sessionId || !session.sessionSlaveId || !slaves) {
    return null;
  }
  return getStickerScopeLeaves(slaves, stickerSlaveId).some((s) => s.id === session.sessionSlaveId)
    ? session.sessionSlaveId
    : null;
};

const LIVE_SESSION_STATES = [
  SessionStatusCodes.REQUESTED,
  SessionStatusCodes.ESTABLISHED,
  SessionStatusCodes.TERMINATION_REQUESTED,
];

export const isLiveSessionState = (state?: number) => state !== undefined && LIVE_SESSION_STATES.includes(state);
